import { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAtom, useSetAtom } from 'jotai'
import {
  addProductToCart,
  removeProductFromCart
} from 'src/redux-api-bridge/cart/effects'
import {
  orderStatusForTrackingSelector,
  selectedDeliverySelector
} from 'src/redux/slices/selectedDelivery/selectors'
import tracking from 'src/shared/tracking'
import { TrackAction } from 'src/componentsV2/CoreUX/actions/trackAction/trackAction.types'
import { getStrikethroughPrice } from 'src/utils/menu'
import { getAddButtonText } from 'src/componentsV2/MenuMealCard/utils_typed'
import { mapProductProps } from 'src/redux-api-bridge/mealsList/utils'
import { useMembershipLimit } from '../useMembershipLimit'
import {
  getNoExtraMealPlanIsCompleted,
  getOrderMealsCountWithStockForHome
} from 'src/redux-api-bridge/order/selectors'
import { cartByDateSelector } from 'src/redux-api-bridge/cart/selectors'
import { ProductCartItem, Meal } from 'src/types/meal'
import { hydrateMealWithPromotion } from 'src/pages/orders/utils'
import { useBoxOrderingModel } from '../useBoxOrderingModel'
import { getIsFlexUserPlan } from 'src/modules/MyAccount/components/Settings/selectors'
import { useIsUnityPassActive } from '../useIsUnityPassActive'
import { mapMenuPromotion } from 'src/hooks/useMenuPromotion'
import { useFlag } from '@unleash/proxy-client-react'
import { FF_USE_BOM_MULTIPLE_PRICES } from 'src/constants/feature-flags'
import { isAddOn } from 'src/modules/MyPlan/utils'
import { getMealsInDelivery } from 'src/modules/MyPlan/selectors'
import { unskipOrderModalAtom } from 'src/atoms/modals/unskipOrderModal.atom'
import { editOrderModalAtom } from 'src/atoms/modals/editOrderModal.atom'

export const useMealActions = (
  product: ProductCartItem,
  onAdd: TrackAction[] | undefined,
  onRemove: TrackAction[] | undefined
) => {
  const selectedDelivery = useSelector(selectedDeliverySelector)
  const orderStatusForTracking = useSelector(orderStatusForTrackingSelector)
  const dispatch = useDispatch()
  const setUnskipOrderModal = useSetAtom(unskipOrderModalAtom)
  const [editOrderModal, setEditOrderModal] = useAtom(editOrderModalAtom)
  const { showPremiumFee } = useMembershipLimit()
  const isFlexUser = useSelector(getIsFlexUserPlan)
  const { currentBox } = useBoxOrderingModel()
  const isUnityPassActive = useIsUnityPassActive()
  const noExtraMealPlanIsCompleted = useSelector(getNoExtraMealPlanIsCompleted)
  const cart = useSelector(cartByDateSelector)
  const useBOMMultiplePrices = useFlag(FF_USE_BOM_MULTIPLE_PRICES)
  const [meal, setMeal] = useState<Meal>(mapProductProps(product))
  const mealsInDelivery = useSelector(getMealsInDelivery)
  const orderMealsCountWithStock = useSelector(
    getOrderMealsCountWithStockForHome
  )
  const planIsCompleted = orderMealsCountWithStock >= mealsInDelivery

  useEffect(() => {
    const meal = hydrateMealWithPromotion(mapProductProps(product))
    const mealInCart = cart?.find(m => m.inventoryId === meal.inventoryId)
    if (mealInCart) {
      meal.quantity = mealInCart.qty
      meal.qty = mealInCart.qty
    } else {
      meal.quantity = 0
      meal.qty = 0
    }

    setMeal(meal)
  }, [cart, product])

  function handleMealAddWithoutOrderSkippedCheck() {
    if (!selectedDelivery?.date) {
      return
    }

    onAdd?.forEach(action => {
      tracking.track(action.data.tag, action.data.extras)
    })

    dispatch(addProductToCart(selectedDelivery.date, meal, 1))
  }

  function onAddToCart() {
    if (!selectedDelivery?.date) {
      return
    }

    if (
      !editOrderModal.alreadySeen &&
      (orderStatusForTracking === 'confirmed' ||
        orderStatusForTracking === 'confirmed-pending')
    ) {
      setEditOrderModal({
        isOpen: true,
        alreadySeen: false
      })
    }

    if (orderStatusForTracking === 'skipped') {
      setUnskipOrderModal({
        isOpen: true,
        product: meal,
        date: selectedDelivery?.date
      })
    } else {
      handleMealAddWithoutOrderSkippedCheck()
    }
  }

  function onRemoveFromCart() {
    if (!selectedDelivery?.date) {
      return
    }

    if (
      !editOrderModal.alreadySeen &&
      (orderStatusForTracking === 'confirmed' ||
        orderStatusForTracking === 'confirmed-pending')
    ) {
      setEditOrderModal({
        isOpen: true,
        alreadySeen: false
      })
    }

    onRemove?.forEach(action => {
      tracking.track(action.data.tag, action.data.extras)
    })

    dispatch(removeProductFromCart(selectedDelivery.date, meal))
  }

  const isExtra = !!meal.isExtra
  const mealPromotion =
    meal.promotions?.length && meal.promotions.length > 0
      ? mapMenuPromotion(meal.promotions[0])
      : null
  const disableMealsActions = false
  const quantity = meal.qty

  const priceText = getAddButtonText({
    meal,
    planIsCompleted,
    isExtra,
    isFlexUser,
    noExtraMealPlanIsCompleted,
    currentBOMBox: currentBox,
    showPremiumFee,
    useBOMMultiplePrices,
    isUnityPassActive
  })

  const strikethroughPrice = getStrikethroughPrice({
    meal,
    menuPromotion: mealPromotion,
    planIsCompleted,
    isBundle: false
  })

  return {
    priceText,
    strikethroughPrice,
    reachedPlanSize: noExtraMealPlanIsCompleted && !isExtra,
    disableAllButtons: disableMealsActions && quantity === 0,
    showPremiumFee,
    quantity,
    add: onAddToCart,
    remove: onRemoveFromCart,
    meal,
    isExtra: isAddOn(meal),
    planIsCompleted
  }
}
